import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById, allVideos } from '@/mocks/categoryData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500 mb-4">{t('error')}</p>
            <Link to="/" className="text-sm text-primary-500 font-medium hover:text-primary-600 transition-colors">
              {t('article.backToList')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = (article.related || [])
    .map((rid: number) => allVideos.find((v) => v.id === rid))
    .filter(Boolean);

  const categoryInfo = article.category === 'movie' ? { slug: 'movie', name: '电影' }
    : article.category === 'tv' ? { slug: 'tv', name: '电视剧' }
    : article.category === 'variety' ? { slug: 'variety', name: '综艺' }
    : article.category === 'anime' ? { slug: 'anime', name: '动漫' }
    : article.category === 'documentary' ? { slug: 'documentary', name: '纪录片' }
    : article.category === 'kids' ? { slug: 'kids', name: '少儿' }
    : article.category === 'sports' ? { slug: 'sports', name: '体育' }
    : article.category === 'music' ? { slug: 'music', name: '音乐' }
    : { slug: 'all', name: article.categoryName };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 pb-16">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">{t('breadcrumb.home')}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${categoryInfo.slug}`} className="hover:text-primary-500 transition-colors">{categoryInfo.name}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
            </nav>

            {/* Header */}
            <div className="relative aspect-[2/1] rounded-xl overflow-hidden mb-8">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-6">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
                    article.isVip
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-secondary-500 text-background-50'
                  }`}>
                    {article.isVip ? 'VIP' : '免费'}
                  </span>
                  <span className="text-xs text-background-50/80">{categoryInfo.name}</span>
                  <span className="text-xs text-background-50/80">{article.duration}</span>
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-background-50">{article.title}</h1>
              </div>
            </div>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
              <span><i className="ri-user-line mr-1"></i>{article.author}</span>
              <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              <span><i className="ri-eye-line mr-1"></i>{article.views}{t('article.views')}</span>
              <span className="text-accent-500 font-medium"><i className="ri-star-fill mr-1"></i>{article.rating}</span>
            </div>

            {/* Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none mb-10 text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-10">
                <span className="text-sm text-foreground-500 mr-1">{t('article.tags')}：</span>
                {article.tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Related */}
          {relatedArticles.length > 0 && (
            <section className="max-w-6xl mx-auto mt-12 pt-10 border-t border-background-200/70">
              <h2 className="text-xl font-bold text-foreground-950 mb-6">{t('article.related')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((video: any) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group rounded-xl overflow-hidden bg-background-100 hover:scale-[1.02] transition-all"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
                          video.isVip
                            ? 'bg-accent-500 text-background-50'
                            : 'bg-secondary-500 text-background-50'
                        }`}>
                          {video.isVip ? 'VIP' : '免费'}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {video.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-accent-500 text-xs"><i className="ri-star-fill mr-0.5"></i>{video.rating}</span>
                        <span className="text-xs text-foreground-400">{video.views}次观看</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
}